const ValidatorType = {
    FILE: 'file',
    IMAGE: 'image'
};

export function validateFile(file, validationType) {
    const validator = getFileValidator(validationType);

    return new Promise((resolve) => {
        validator.validate(file).then(validationErrors => {
            resolve(validationErrors);
        });
    });
}

function getFileValidator(type) {
    switch(type) {
        case ValidatorType.IMAGE:
            if (
                !window.validationImageMaxSizeInMB || 
                !window.validationImageMinHeightPx || 
                !window.validationImageMinWidthPx
            ) {
                throw new Error("Image validation data not provided");
            }

            return new FileValidator({
                type,
                allowedTypes: ['image/jpeg'],
                maxSizeInMB: window.validationImageMaxSizeInMB,
                minHeightPx: window.validationImageMinHeightPx,
                minWidthPx: window.validationImageMinWidthPx
            });
        case ValidatorType.FILE:
            if (!window.validationFileMaxSizeInMB) {
                throw new Error("File validation data not provided");
            }

            return new FileValidator({
                type,
                allowedTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/svg'],
                maxSizeInMB: window.validationFileMaxSizeInMB
            });
        default:
            throw new Error("Unknown validator type");
      }
}

class FileValidator {
    constructor({ type, allowedTypes, maxSizeInMB, minWidthPx, minHeightPx  }) {
        this.type = type;
        this.allowedTypes = allowedTypes;
        this.maxSizeInMB = maxSizeInMB;
        this.minWidthPx = minWidthPx;
        this.minHeightPx = minHeightPx;
    }

    validate(file) {
        const validationErrors = [];
        return new Promise((resolve) => {
            if (this.allowedTypes) {
                const error = this.validateAllowedTypes(file);
                if (error) validationErrors.push(error);
            }

            if (this.maxSizeInMB) {
                const error = this.validateMaxSize(file);
                if (error) validationErrors.push(error);
            }

            if (!this.shouldValidateDimensions(file)) resolve(validationErrors);

            if (this.minWidthPx || this.minHeightPx) {
                this.getImage(file).then((image) => {
                    if (this.minWidthPx) {
                        const error = this.validateMinWidth(image);
                        if (error) validationErrors.push(error);
                    }

                    if (this.minHeightPx) {
                        const error = this.validateMinHeight(image);
                        if (error) validationErrors.push(error);
                    }

                    resolve(validationErrors);
                });
            }
        });
    }

    shouldValidateDimensions(file) {
        if (this.type !== ValidatorType.IMAGE) {
            return false;
        }

        return this.isFileImage(file);
    }

    isFileImage(file) {
        return file.type.indexOf('image/') === 0;
    }

    validateAllowedTypes(file) {
        const isFileTypeAllowed = this.allowedTypes.includes(file.type);

        if (isFileTypeAllowed) return false;

        return Translator.trans('invalid_file_type', { allowedTypes: this.allowedTypes }, 'validators');
    }

    validateMaxSize(file) {
        if (!file.size) {
            return Translator.trans('unable_to_check_size', {}, 'validators');
        }

        const fileSizeMB = (file.size / (1024 * 1024));
        const isSizeBelowMax = fileSizeMB < this.maxSizeInMB;

        if (isSizeBelowMax) return false;

        return Translator.trans('file_too_big', {limit: this.maxSizeInMB, suffix: 'MB'}, 'validators');
    }

    validateMinWidth(image) {
        const { width } = image;
        const isWidthValid = width >= this.minWidthPx;

        if (isWidthValid) return false;

        return Translator.trans('width_too_small', { width: width, minWidth: this.minWidthPx }, 'validators');
    }

    validateMinHeight(image) {
        const { height } = image;
        const isHeightValid = height >= this.minHeightPx;

        if (isHeightValid) return false;

        return Translator.trans('height_too_small', { height: height, minHeight: this.minHeightPx }, 'validators');
    }

    getImage(file) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (e) => {
                const image = new Image();
                image.src = e.target.result;
                image.onload = function() {
                    reader.abort();
                    resolve(this);
                };
            };
        });
    }
}